/**
 * CirclesOfCareSheet.jsx
 *
 * Feb 23 2026 · Michael. Before the donor hits Stripe, offer three
 * ways to send the light onwards:
 *
 *   · "Solo"   — keep it (default; matches pre-Circles flow).
 *   · "Named"  — give to specific people (up to `lights` names).
 *                Strict 1 light = 1 recipient. Optional email + note.
 *   · "Pool"   — hand it to us to allocate to a carer / caree.
 *                Optional preference + dedication note.
 *
 * Anonymous toggle at the bottom lets the donor stay private.
 *
 * The sheet is *purely* a UX layer: it collects the payload and
 * hands it back via `onConfirm` for the parent to forward into the
 * existing POST /api/oml/checkout.
 */
import React, { useMemo, useState } from "react";

const TOK = {
  bg: "radial-gradient(ellipse at 50% 22%, #1a2e50 0%, #0d1f3a 50%, #060e1f 85%)",
  cream: "#f5ecd9",
  creamStrong: "rgba(245,236,217,0.94)",
  creamDim: "rgba(245,236,217,0.72)",
  creamMist: "rgba(245,236,217,0.5)",
  gold: "#e0a97a",
  goldWarm: "#f0c790",
  goldSoft: "rgba(224,169,122,0.9)",
  goldMist: "rgba(224,169,122,0.24)",
  goldGlow: "rgba(224,169,122,0.32)",
  divider: "rgba(245,236,217,0.1)",
};
const SERIF = '"Playfair Display", "Cormorant Garamond", serif';
const SANS = '"Manrope", "Helvetica Neue", Helvetica, Arial, sans-serif';

// Feb 27 2026 · Michael: "For OML, let's change the order — so
// the first card they see is 'I have someone in mind'." Reordered
// so the intent-forward option leads the sheet. The default `mode`
// state below is updated to match so this card is pre-selected.
const MODE_CARDS = [
  {
    key: "named",
    label: "I have someone in mind",
    blurb: "Give it to people you name. One light per person.",
  },
  {
    key: "solo",
    label: "Keep this light",
    blurb: "Give — decide later. The light waits with us until you're ready.",
  },
  {
    key: "pool",
    label: "Pass it on for me",
    blurb: "We'll place your light with a carer or the person they care for.",
  },
];

const POOL_PREFERENCES = [
  { key: "carer", label: "A carer" },
  { key: "caree", label: "Someone being cared for" },
  { key: "either", label: "Either — you choose" },
];

export default function CirclesOfCareSheet({ lights, currencySymbol, amount, onConfirm, onCancel }) {
  const [mode, setMode] = useState("named");
  const [anonymous, setAnonymous] = useState(false);
  const [recipients, setRecipients] = useState([{ name: "", email: "", note: "" }]);
  const [poolPreference, setPoolPreference] = useState("either");
  const [poolNote, setPoolNote] = useState("");
  const [errors, setErrors] = useState({});

  const canAddRecipient = mode === "named" && recipients.length < lights;

  const isValid = useMemo(() => {
    if (mode === "solo") return true;
    if (mode === "pool") return true;
    // Named — at least one recipient with a non-empty name; each row
    // has either a name or nothing (we drop empty rows on confirm).
    const named = recipients.filter((r) => (r.name || "").trim());
    return named.length > 0 && named.length <= lights;
  }, [mode, recipients, lights]);

  const updateRecipient = (i, patch) => {
    setRecipients((rs) => rs.map((r, idx) => (idx === i ? { ...r, ...patch } : r)));
  };

  const addRecipient = () => {
    if (!canAddRecipient) return;
    setRecipients((rs) => [...rs, { name: "", email: "", note: "" }]);
  };

  const removeRecipient = (i) => {
    setRecipients((rs) => (rs.length > 1 ? rs.filter((_, idx) => idx !== i) : rs));
  };

  const handleConfirm = () => {
    if (!isValid) {
      setErrors({ recipients: "Please add at least one name." });
      return;
    }
    const payload = { gift_mode: mode, anonymous };
    if (mode === "named") {
      payload.recipients = recipients
        .filter((r) => (r.name || "").trim())
        .slice(0, lights)
        .map((r) => ({
          name: r.name.trim(),
          email: (r.email || "").trim() || undefined,
          note: (r.note || "").trim() || undefined,
        }));
    }
    if (mode === "pool") {
      payload.pool_preference = poolPreference;
      payload.pool_note = poolNote.trim() || undefined;
    }
    onConfirm(payload);
  };

  return (
    <div
      className="fixed inset-0 z-50 overflow-y-auto"
      style={{ background: TOK.bg, color: TOK.cream }}
      data-testid="circles-of-care-sheet"
    >
      <div className="max-w-md mx-auto px-5 pt-6 pb-24">
        {/* Close */}
        <div className="flex justify-between items-center">
          <button
            onClick={onCancel}
            className="uppercase"
            style={{
              background: "transparent", color: TOK.creamMist, fontFamily: SANS,
              fontSize: "0.58rem", letterSpacing: "0.28em", padding: "10px 4px", minHeight: 44,
            }}
            data-testid="circles-cancel"
          >
            ← Back
          </button>
          <div
            className="uppercase"
            style={{
              color: TOK.goldSoft, fontFamily: SANS, fontSize: "0.55rem", letterSpacing: "0.3em",
            }}
          >
            Circles of Care
          </div>
        </div>

        <h2
          className="mt-8 italic text-center"
          style={{
            fontFamily: SERIF,
            fontSize: "clamp(1.75rem, 6vw, 2.1rem)",
            fontWeight: 300,
            lineHeight: 1.2,
            // Feb 27 2026 · Michael flagged the heading was rendering
            // in dark ink on the dark navy sheet on iOS — an inherited
            // colour from the parent modal. Force cream + soft glow so
            // the title reads at every scroll position.
            color: TOK.cream,
            textShadow: "0 0 20px rgba(224,169,122,0.18)",
          }}
          data-testid="circles-title"
        >
          {lights === 1 ? "A light with a name." : `${lights} lights — with names or without.`}
        </h2>
        <p
          className="mt-4 text-center max-w-sm mx-auto"
          style={{ color: TOK.creamStrong, fontFamily: SANS, fontSize: "0.95rem", lineHeight: 1.55 }}
        >
          {currencySymbol}{amount} · one light per person. Every light lands with Soulumo
          Foundations for someone who could use a little care.
        </p>

        {/* Mode cards */}
        <div className="mt-8 space-y-3" data-testid="circles-mode-list">
          {MODE_CARDS.map((c) => {
            const active = mode === c.key;
            return (
              <button
                key={c.key}
                type="button"
                onClick={() => setMode(c.key)}
                data-testid={`circles-mode-${c.key}`}
                className="w-full rounded-2xl text-left transition-colors"
                style={{
                  background: active
                    ? "linear-gradient(135deg, rgba(240,199,144,0.18) 0%, rgba(224,169,122,0.08) 100%)"
                    : "rgba(245,236,217,0.035)",
                  border: `1px solid ${active ? TOK.goldSoft : TOK.goldMist}`,
                  color: TOK.cream,
                  padding: "16px 18px",
                  minHeight: 76,
                  cursor: "pointer",
                }}
              >
                <div
                  className="italic"
                  style={{
                    fontFamily: SERIF, fontSize: "1.15rem", color: TOK.cream,
                    fontWeight: 300,
                  }}
                >
                  {c.label}
                </div>
                <div
                  className="mt-1"
                  style={{
                    color: TOK.creamStrong, fontFamily: SANS,
                    fontSize: "0.85rem", lineHeight: 1.45,
                  }}
                >
                  {c.blurb}
                </div>
              </button>
            );
          })}
        </div>

        {/* Named recipients form */}
        {mode === "named" && (
          <div className="mt-6" data-testid="circles-named-form">
            <div
              className="uppercase mb-3"
              style={{
                color: TOK.goldSoft, fontFamily: SANS,
                fontSize: "0.58rem", letterSpacing: "0.28em",
              }}
            >
              {recipients.filter((r) => r.name.trim()).length}/{lights} lights named
            </div>
            <div className="space-y-3">
              {recipients.map((r, i) => (
                <div
                  key={i}
                  className="rounded-2xl p-4"
                  style={{
                    background: "rgba(245,236,217,0.03)",
                    border: `1px solid ${TOK.goldMist}`,
                  }}
                  data-testid={`circles-recipient-${i}`}
                >
                  <input
                    type="text"
                    value={r.name}
                    onChange={(e) => updateRecipient(i, { name: e.target.value })}
                    placeholder="First name"
                    className="w-full rounded-lg px-3 py-2"
                    style={{
                      background: "rgba(245,236,217,0.05)",
                      border: `1px solid ${TOK.goldMist}`,
                      color: TOK.cream,
                      fontFamily: SANS,
                      fontSize: "0.95rem",
                      outline: "none",
                    }}
                    maxLength={80}
                    data-testid={`circles-recipient-${i}-name`}
                  />
                  <input
                    type="email"
                    value={r.email}
                    onChange={(e) => updateRecipient(i, { email: e.target.value })}
                    placeholder="Email (optional — sends them the invite)"
                    className="w-full rounded-lg px-3 py-2 mt-2"
                    style={{
                      background: "rgba(245,236,217,0.05)",
                      border: `1px solid ${TOK.goldMist}`,
                      color: TOK.cream,
                      fontFamily: SANS,
                      fontSize: "0.9rem",
                      outline: "none",
                    }}
                    maxLength={180}
                    data-testid={`circles-recipient-${i}-email`}
                  />
                  <input
                    type="text"
                    value={r.note}
                    onChange={(e) => updateRecipient(i, { note: e.target.value })}
                    placeholder='Short note ("For a difficult year")'
                    className="w-full rounded-lg px-3 py-2 mt-2"
                    style={{
                      background: "rgba(245,236,217,0.05)",
                      border: `1px solid ${TOK.goldMist}`,
                      color: TOK.cream,
                      fontFamily: SANS,
                      fontSize: "0.88rem",
                      outline: "none",
                    }}
                    maxLength={280}
                    data-testid={`circles-recipient-${i}-note`}
                  />
                  {recipients.length > 1 && (
                    <button
                      type="button"
                      onClick={() => removeRecipient(i)}
                      className="mt-2 uppercase"
                      style={{
                        background: "transparent", color: TOK.creamMist,
                        fontFamily: SANS, fontSize: "0.56rem", letterSpacing: "0.28em",
                        padding: "6px 2px",
                      }}
                      data-testid={`circles-recipient-${i}-remove`}
                    >
                      Remove
                    </button>
                  )}
                </div>
              ))}
              {canAddRecipient && (
                <button
                  type="button"
                  onClick={addRecipient}
                  className="w-full rounded-2xl uppercase"
                  style={{
                    background: "transparent",
                    border: `1px dashed ${TOK.goldMist}`,
                    color: TOK.goldSoft,
                    fontFamily: SANS,
                    fontSize: "0.68rem",
                    letterSpacing: "0.24em",
                    padding: "14px 12px",
                    minHeight: 52,
                  }}
                  data-testid="circles-add-recipient"
                >
                  + Add another person ({lights - recipients.length} lights left)
                </button>
              )}
              {errors.recipients && (
                <div
                  className="text-center mt-2"
                  style={{ color: "#e07a6a", fontFamily: SANS, fontSize: "0.85rem" }}
                  data-testid="circles-recipient-error"
                >
                  {errors.recipients}
                </div>
              )}
            </div>
          </div>
        )}

        {/* Pool form */}
        {mode === "pool" && (
          <div className="mt-6" data-testid="circles-pool-form">
            <div
              className="uppercase mb-3"
              style={{
                color: TOK.goldSoft, fontFamily: SANS,
                fontSize: "0.58rem", letterSpacing: "0.28em",
              }}
            >
              Preference
            </div>
            <div className="space-y-2">
              {POOL_PREFERENCES.map((p) => {
                const active = poolPreference === p.key;
                return (
                  <button
                    key={p.key}
                    type="button"
                    onClick={() => setPoolPreference(p.key)}
                    data-testid={`circles-pool-pref-${p.key}`}
                    className="w-full rounded-full text-left"
                    style={{
                      background: active ? "rgba(224,169,122,0.16)" : "rgba(245,236,217,0.04)",
                      border: `1px solid ${active ? TOK.goldSoft : TOK.goldMist}`,
                      color: TOK.cream,
                      padding: "12px 20px",
                      minHeight: 48,
                      fontFamily: SANS,
                      fontSize: "0.95rem",
                    }}
                  >
                    {p.label}
                  </button>
                );
              })}
            </div>
            <textarea
              value={poolNote}
              onChange={(e) => setPoolNote(e.target.value)}
              placeholder="Optional dedication note we can include in the invite."
              maxLength={280}
              rows={3}
              className="w-full rounded-2xl mt-3 px-3 py-3"
              style={{
                background: "rgba(245,236,217,0.05)",
                border: `1px solid ${TOK.goldMist}`,
                color: TOK.cream,
                fontFamily: SANS,
                fontSize: "0.92rem",
                outline: "none",
                resize: "vertical",
              }}
              data-testid="circles-pool-note"
            />
          </div>
        )}

        {/* Anonymous toggle */}
        {mode !== "solo" && (
          <button
            type="button"
            onClick={() => setAnonymous(!anonymous)}
            className="mt-6 w-full flex items-center justify-between rounded-full"
            style={{
              background: anonymous ? "rgba(224,169,122,0.12)" : "rgba(245,236,217,0.03)",
              border: `1px solid ${anonymous ? TOK.goldSoft : TOK.goldMist}`,
              color: TOK.cream,
              padding: "12px 20px",
              minHeight: 48,
            }}
            data-testid="circles-anonymous-toggle"
          >
            <span style={{ fontFamily: SANS, fontSize: "0.9rem" }}>
              {anonymous ? "Sending anonymously" : "Send with my name"}
            </span>
            <span
              className="uppercase"
              style={{
                color: TOK.goldSoft, fontFamily: SANS,
                fontSize: "0.58rem", letterSpacing: "0.28em",
              }}
            >
              {anonymous ? "On" : "Off"}
            </span>
          </button>
        )}

        {/* CTA */}
        <button
          type="button"
          onClick={handleConfirm}
          disabled={!isValid}
          className="mt-8 w-full rounded-full flex items-center justify-center"
          style={{
            background: isValid
              ? "linear-gradient(180deg, #f0c790 0%, #d99b6b 100%)"
              : "rgba(245,236,217,0.1)",
            border: "1px solid #f5e0c5",
            color: isValid ? "#1a0f05" : TOK.creamMist,
            padding: "18px 22px",
            minHeight: 64,
            fontFamily: SANS,
            fontSize: "0.86rem",
            fontWeight: 700,
            letterSpacing: "0.22em",
            textTransform: "uppercase",
            boxShadow: isValid ? `0 12px 44px ${TOK.goldGlow}` : "none",
            cursor: isValid ? "pointer" : "not-allowed",
          }}
          data-testid="circles-confirm"
        >
          Continue to payment
        </button>
      </div>
    </div>
  );
}
